/**
 * Admin moderation: flagged calendar uploads + user reports.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { apiJson } from './api';

const TABS = [
  { id: 'flagged', label: 'Flagged uploads' },
  { id: 'all', label: 'All uploads' },
  { id: 'reports', label: 'User reports' },
];

function fmtDate(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

export default function AdminCalendarModeration() {
  const { user, token } = useAuth();
  const [tab, setTab] = useState('flagged');
  const [uploads, setUploads] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const loadUploads = useCallback(async (status) => {
    setLoading(true); setError('');
    try {
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      const r = await apiJson(`/api/admin/calendar-uploads${qs}`, { token });
      setUploads(r.uploads || []);
    } catch (e) { setError(e.message || 'Failed to load uploads'); }
    finally { setLoading(false); }
  }, [token]);

  const loadReports = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await apiJson('/api/admin/calendar-reports', { token });
      setReports(r.reports || []);
    } catch (e) { setError(e.message || 'Failed to load reports'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (!user?.isAdmin) return;
    if (tab === 'flagged') loadUploads('flagged');
    else if (tab === 'all') loadUploads('');
    else loadReports();
  }, [tab, user, loadUploads, loadReports]);

  async function act(uploadId, action) {
    setBusyId(uploadId); setError('');
    try {
      await apiJson(`/api/admin/calendar-uploads/${uploadId}`, { method: 'PATCH', token, body: { action } });
      setUploads((prev) => prev.filter((u) => u.id !== uploadId));
    } catch (e) { setError(e.message || 'Action failed'); }
    finally { setBusyId(null); }
  }

  if (!user?.isAdmin) return <div className="tt-page"><div className="tt-status error">Admin only</div></div>;

  return (
    <div className="tt-page" style={{ maxWidth: 1100 }}>
      <header className="tt-header">
        <div>
          <h1>Calendar moderation</h1>
          <p className="tt-sub">Review user uploads and reports on shared revision plans.</p>
        </div>
        <div><Link to="/admin/papers" className="secondary-btn">← Papers</Link></div>
      </header>

      <nav className="cal-tabs" role="tablist" aria-label="Moderation tabs">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} className={`cal-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </nav>

      {error && <div className="tt-status error">{error}</div>}
      {loading && <div className="tt-status saving">Loading…</div>}

      {(tab === 'flagged' || tab === 'all') && !loading && (
        uploads.length === 0
          ? <div className="tt-empty">Nothing to review here. ✨</div>
          : (
            <ul className="cal-item-list">
              {uploads.map((u) => (
                <li key={u.id} className={`cal-item-row${u.filterStatus === 'flagged' ? ' flagged' : ''}`} style={{ alignItems: 'flex-start', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0 }}>
                      <div className="cal-picker-title">{u.label || u.originalName}</div>
                      <div className="cal-picker-meta">
                        Uploaded by {u.uploaderName || 'unknown'} · {fmtDate(u.createdAt)} · {(u.sizeBytes / 1024 / 1024).toFixed(2)} MB
                        {u.calendarSlug && <> · <Link to={`/calendars/${u.calendarSlug}`} target="_blank" rel="noreferrer">view in calendar</Link></>}
                      </div>
                    </div>
                    <span className={`cal-tag cal-tag-${u.filterStatus === 'flagged' ? 'unlisted' : 'public'}`}>{u.filterStatus}</span>
                  </div>
                  {u.filterReason && <div style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>Reason: {u.filterReason}</div>}
                  <div className="cal-edit-actions" style={{ width: '100%' }}>
                    {u.paperId && <Link to={`/viewer/${u.paperId}`} target="_blank" rel="noreferrer" className="secondary-btn">Open PDF</Link>}
                    <button type="button" className="primary-btn" disabled={busyId === u.id} onClick={() => act(u.id, 'approve')}>Approve</button>
                    <button type="button" className="cal-link-btn cal-link-danger" disabled={busyId === u.id} onClick={() => act(u.id, 'remove')}>Remove</button>
                  </div>
                </li>
              ))}
            </ul>
          )
      )}

      {tab === 'reports' && !loading && (
        reports.length === 0
          ? <div className="tt-empty">No reports.</div>
          : (
            <ul className="cal-item-list">
              {reports.map((r) => (
                <li key={r.id} className="cal-item-row" style={{ alignItems: 'flex-start', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0 }}>
                      <div className="cal-picker-title">{r.calendarTitle || '(deleted calendar)'}</div>
                      <div className="cal-picker-meta">
                        Reported by {r.reporterName || 'guest'} · {fmtDate(r.createdAt)}
                        {r.calendarSlug && <> · <Link to={`/calendars/${r.calendarSlug}`} target="_blank" rel="noreferrer">open</Link></>}
                      </div>
                    </div>
                  </div>
                  <div style={{ color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{r.reason}</div>
                </li>
              ))}
            </ul>
          )
      )}
    </div>
  );
}
